import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import StatusBadge from "@/components/base/StatusBadge";
import Rating from "@/components/base/Rating";
import EmptyState from "@/components/base/EmptyState";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import { useAuth } from "@/context/AuthContext";
import { deleteOwnerListing, getOwnerListings } from "@/services/ownerApi";
import type { OwnedListing } from "@/types";

const statusFilters = [
  { value: "all", label: "All" },
  { value: "approved", label: "Approved" },
  { value: "pending", label: "Pending" },
];

export default function MyListings() {
  const { token } = useAuth();
  const [listings, setListings] = useState<OwnedListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [status, setStatus] = useState("all");
  const [query, setQuery] = useState("");
  const [pendingDelete, setPendingDelete] = useState<OwnedListing | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    getOwnerListings(token)
      .then((l) => active && setListings(l))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return listings.filter((l) => {
      const matchesStatus = status === "all" || l.status === status;
      const matchesQuery = !q || l.name.toLowerCase().includes(q) || l.categoryName.toLowerCase().includes(q);
      return matchesStatus && matchesQuery;
    });
  }, [listings, status, query]);

  const counts = useMemo(
    () => ({
      all: listings.length,
      approved: listings.filter((l) => l.status === "approved").length,
      pending: listings.filter((l) => l.status === "pending").length,
    }),
    [listings],
  );

  const confirmDelete = async () => {
    const target = pendingDelete;
    if (!target) return;
    setDeleting(true);
    const snapshot = listings;
    setListings((prev) => prev.filter((l) => l.id !== target.id));
    const ok = await deleteOwnerListing(target.id, token);
    setDeleting(false);
    setPendingDelete(null);
    if (!ok) {
      setListings(snapshot);
      setNotice("We couldn't delete that listing. Please try again.");
    } else {
      setNotice(`“${target.name}” was deleted.`);
    }
    setTimeout(() => setNotice(""), 2600);
  };

  return (
    <>
      <Seo title="My Listings" path="/dashboard/listings" />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-foreground-950">My listings</h1>
          <p className="mt-1 text-sm text-foreground-600">Create, edit and monitor every business you manage.</p>
        </div>
        <Link
          to="/dashboard/listings/new"
          className="inline-flex w-fit items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-add-circle-line"></i> Add listing
        </Link>
      </div>

      {notice && (
        <p className="mt-4 inline-flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-2 text-sm text-accent-900">
          <i className="ri-checkbox-circle-line text-accent-600"></i> {notice}
        </p>
      )}

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="inline-flex items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1">
          {statusFilters.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setStatus(f.value)}
              className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                status === f.value ? "bg-primary-500 text-foreground-950" : "text-foreground-600 hover:bg-background-100"
              }`}
            >
              {f.label} <span className="text-xs opacity-70">{counts[f.value as keyof typeof counts]}</span>
            </button>
          ))}
        </div>
        <div className="relative sm:w-72">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search your listings…"
            className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-3 text-sm focus:border-primary-400 focus:outline-none"
          />
        </div>
      </div>

      <div className="mt-5">
        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-40 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
            ))}
          </div>
        ) : error ? (
          <EmptyState icon="ri-wifi-off-line" title="Couldn't load your listings" description="Please refresh the page and try again." />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="ri-store-2-line"
            title="No listings found"
            description="Try a different filter, or add a new business listing to get started."
          />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filtered.map((l) => (
              <article key={l.id} className="flex flex-col rounded-lg border border-background-200 bg-background-50 overflow-hidden">
                <div className="flex gap-4 p-4">
                  <Link to={`/dashboard/listings/${l.id}`} className="block h-24 w-24 shrink-0 overflow-hidden rounded-md">
                    <img src={l.image} alt={l.name} className="h-full w-full object-cover" />
                  </Link>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-secondary-700">{l.categoryName}</span>
                        <h2 className="truncate font-heading text-base font-bold text-foreground-950">
                          <Link to={`/dashboard/listings/${l.id}`} className="hover:text-primary-700">{l.name}</Link>
                        </h2>
                      </div>
                      <StatusBadge status={l.status} />
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
                      <span className="inline-flex items-center gap-1"><i className="ri-map-pin-2-line"></i> {l.locationName}</span>
                      <span className="inline-flex items-center gap-1"><i className="ri-eye-line"></i> {l.views.toLocaleString()}</span>
                      <span className="inline-flex items-center gap-1"><i className="ri-user-received-line"></i> {l.leads} leads</span>
                    </div>
                    <div className="mt-1.5">
                      {l.reviewCount > 0 ? <Rating value={l.rating} count={l.reviewCount} /> : <span className="text-xs text-foreground-400">No reviews yet</span>}
                    </div>
                  </div>
                </div>
                <div className="mt-auto flex items-center justify-between border-t border-background-100 px-4 py-3">
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-foreground-600">
                    <i className="ri-vip-crown-line text-primary-600"></i> {l.plan}
                  </span>
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/business/${l.slug}`}
                      className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-1.5 text-xs font-semibold text-foreground-700 hover:border-primary-400 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-external-link-line"></i> View
                    </Link>
                    <Link
                      to={`/dashboard/listings/${l.id}`}
                      className="inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-3 py-1.5 text-xs font-semibold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-edit-line"></i> Edit
                    </Link>
                    <button
                      type="button"
                      onClick={() => setPendingDelete(l)}
                      className="inline-flex items-center gap-1.5 rounded-md border border-secondary-300 px-3 py-1.5 text-xs font-semibold text-secondary-700 hover:bg-secondary-50 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-delete-bin-line"></i> Delete
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete this listing?"
        message={pendingDelete ? `“${pendingDelete.name}” and its reviews will be permanently removed. This cannot be undone.` : ""}
        confirmLabel="Delete listing"
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
}